// One-source runtime compile/apply flow for prepared source entries.
// It preserves debug/map/performance behavior across main and worker execution.
import { dispatchCompileWithContext } from '../../compile-dispatch/dispatch'
import { setCompileHistory } from '../../compile-dispatch/history'
import { writeCssToStyleTag } from '../../style-sheet'
import { getNow } from '../context-time'
import type { LuisCompileContext } from '../context'
import { readLuisRuntimeOptions, readLuisStyleOutputMode } from '../options'
import type { BuildSourceEntry } from './types'
import { getRuntimeFeature } from '../../features'
import type { LuisMapFeatureApi } from '../../features'
import { markBuildError, markBuildSuccess } from './reporting'
import { buildPublishTiming, clearDebugAndObservedState, ensureObservedFeatureForCompile, isBuildRevisionCurrent } from './compile-entry-helpers'
import { assertCompileResultSafe } from './compile-entry-security'
import { prepareCompiledCss } from './prepare-css'
import { publishRuntimeDiagnostics } from './publish-diagnostics'
import { LuisSecurityError } from '@lentystyle/guard/runtime'
import { LuisStaleCompileError } from '../../compile-dispatch/dispatch'

// Process one prepared source entry and update compile context if successful.
export async function processBuildSourceEntry(
  entry: BuildSourceEntry,
  context: LuisCompileContext,
  buildRevision: number,
): Promise<boolean> {
  const options = readLuisRuntimeOptions(entry.source)
  const outputMode = readLuisStyleOutputMode(entry.source)
  const debugFeature = options.debug || options.map ? getRuntimeFeature('debug') : undefined
  const mapFeature = options.map ? getRuntimeFeature('map') : undefined
  const performanceFeature = options.performance ? getRuntimeFeature('performance') : undefined
  const debugEnabled = options.debug && Boolean(debugFeature)
  const mapEnabled = options.map && Boolean(mapFeature)
  const performanceEnabled = options.performance && Boolean(performanceFeature)
  const compileDebugEnabled = debugEnabled || mapEnabled

  // Mark source as failed when link fetch failed during entry build.
  if (entry.fetchError) {
    cleanupBuildSideEffects(entry.styleTag, mapFeature)
    clearDebugAndObservedState(entry.styleTag.id)
    markBuildError(entry, entry.fetchError, debugEnabled)
    // Return false after fetch-stage failure handling.
    return false
  }

  try {
    const compileSourceId = entry.kind === 'link' ? entry.href : entry.sourceId
    const processStart = getNow()
    const compile = await dispatchCompileWithContext(entry, context, compileDebugEnabled, { worker: options.worker })
    // Check whether build revision is still current after compile.
    if (!isBuildRevisionCurrent(buildRevision)) {
      // Return false for superseded compile results.
      return false
    }
    const compileWarnings = entry.preCompileWarnings.length > 0
      ? [...entry.preCompileWarnings, ...compile.result.warnings]
      : compile.result.warnings
    assertCompileResultSafe(compile.result)
    const observedRuntime = await ensureObservedFeatureForCompile(
      compile.result.observedRules?.length ?? 0,
      compile.result.initialObservedRules?.length ?? 0,
      options.lazy,
    )
    const lazyEnabled = options.lazy && Boolean(observedRuntime.lazyFeature)
    // Check whether build revision is still current after feature loading.
    if (!isBuildRevisionCurrent(buildRevision)) {
      // Return false for superseded feature-load results.
      return false
    }

    const preparedCss = prepareCompiledCss(
      compile.result,
      entry,
      {
        compileSourceId,
        mapEnabled,
        lazyEnabled,
        outputMode,
        compileDebugEnabled,
        debugFeature,
        mapFeature,
      },
    )
    // Check whether build revision is still current after CSS preparation.
    if (!isBuildRevisionCurrent(buildRevision)) {
      // Return false for superseded CSS preparation results.
      return false
    }
    // Check whether runtime map cleanup should run for this source.
    if (!mapEnabled) {
      cleanupBuildSideEffects(entry.styleTag, mapFeature)
    }
    const applyStart = getNow()
    applyCompiledSource(entry, preparedCss._cssWithSourceMap, mapEnabled, performanceEnabled)
    const applyDurationMs = getNow() - applyStart
    const totalDurationMs = getNow() - processStart
    // Check whether build revision is still current before publish.
    if (!isBuildRevisionCurrent(buildRevision)) {
      // Return false for superseded publish results.
      return false
    }
    setCompileHistory(compileSourceId, { durationMs: totalDurationMs, execution: compile.execution })
    markBuildSuccess(entry)
    publishRuntimeDiagnostics(entry, compileSourceId, compileWarnings, preparedCss._remappedDebugRules, preparedCss._cssWithDebugMarkers.length, {
      compile,
      timing: buildPublishTiming(compile, applyDurationMs, totalDurationMs, observedRuntime.featureLoadDurationMs, preparedCss._timing),
      debugEnabled,
      mapEnabled,
      performanceEnabled,
      lazyEnabled,
      debugFeature,
      mapFeature,
      lazyFeature: observedRuntime.lazyFeature,
      performanceFeature,
      observedFeature: observedRuntime.observedFeature,
    })
    // Return true after successful compile and publish flow.
    return true
  } catch (error) {
    // Check whether current failure is only a stale compile cancellation.
    if (error instanceof LuisStaleCompileError) {
      // Return false for stale compile cancellations.
      return false
    }
    cleanupBuildSideEffects(entry.styleTag, mapFeature)
    clearDebugAndObservedState(entry.styleTag.id)
    markBuildError(entry, error, debugEnabled)
    // Check whether current failure should still throw for security reasons.
    if (error instanceof LuisSecurityError) {
      throw error
    }
    // Return false after non-security compile failure handling.
    return false
  }
}

// Write CSS to DOM and clean stale source-map state when disabled.
function applyCompiledSource(entry: BuildSourceEntry, cssWithSourceMap: string, mapEnabled: boolean, performanceEnabled: boolean): void {
  writeCssToStyleTag(entry.styleTag, cssWithSourceMap, { forceTextContent: mapEnabled, preferCssom: performanceEnabled })
}

// Cleanup source-map state with plugin fallback cleanup.
function cleanupBuildSideEffects(styleTag: HTMLStyleElement, mapFeature: LuisMapFeatureApi | undefined): void {
  if (mapFeature) {
    mapFeature.revokeLuisSourceMapForStyleTag(styleTag)
  } else {
    // Skip fallback map cleanup when map feature module is not loaded.
  }
}
